import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../layouts/MainLayout';
import { userService } from '../../services/api/userService';
import type { User, UserRole, UserStatus } from '../../types';

const roleConfig: Record<UserRole, { label: string; color: 'info' | 'success' | 'warning' }> = {
  admin: { label: '管理者', color: 'info' },
  reviewer: { label: 'レビュアー', color: 'warning' },
  user: { label: '一般ユーザー', color: 'success' },
};

const statusConfig: Record<UserStatus, { label: string; color: 'success' | 'error' }> = {
  active: { label: '有効', color: 'success' },
  inactive: { label: '無効', color: 'error' },
};

export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [roleChangeOpen, setRoleChangeOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('user');
  const [newRole, setNewRole] = useState<UserRole>('user');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ユーザー一覧を取得
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setMessage(null);
    try {
      const fetchedUsers = await userService.getUsers();
      setUsers(fetchedUsers);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ユーザー一覧の取得に失敗しました';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初回ロード
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ユーザー招待
  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setIsSubmitting(true);
    setMessage(null);
    try {
      await userService.inviteUser(inviteEmail, inviteRole);
      setMessage({ type: 'success', text: `${inviteEmail} に招待メールを送信しました` });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('user');
      // 招待後はユーザー一覧を更新（招待ユーザーが登録済みの場合に備えて）
      await fetchUsers();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '招待メールの送信に失敗しました';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 権限変更
  const handleRoleChange = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    setMessage(null);
    try {
      const updatedUser = await userService.updateUserRole(selectedUser.id, newRole);
      setUsers((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? updatedUser : u))
      );
      setMessage({ type: 'success', text: `${selectedUser.name} の権限を変更しました` });
      setRoleChangeOpen(false);
      setSelectedUser(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '権限の変更に失敗しました';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ユーザー削除
  const handleDelete = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    setMessage(null);
    try {
      await userService.deleteUser(selectedUser.id);
      setUsers((prev) => prev.filter((u) => u.id !== selectedUser.id));
      setMessage({ type: 'success', text: `${selectedUser.name} を削除しました` });
      setDeleteConfirmOpen(false);
      setSelectedUser(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'ユーザーの削除に失敗しました';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRoleChangeDialog = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleChangeOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteConfirmOpen(true);
  };

  // テーブルのスケルトンローディング
  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell><Skeleton variant="text" width={100} /></TableCell>
        <TableCell><Skeleton variant="text" width={180} /></TableCell>
        <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
        <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
        <TableCell><Skeleton variant="text" width={100} /></TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight={500}>
            ユーザー管理
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
              disabled={isLoading}
            >
              更新
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              ユーザーを招待
            </Button>
          </Box>
        </Box>

        {/* Message */}
        {message && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {/* User Table */}
        <Paper elevation={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 500 }}>氏名</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>メールアドレス</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>ロール</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>ステータス</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>作成日</TableCell>
                  <TableCell sx={{ fontWeight: 500 }} align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  renderSkeletonRows()
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        ユーザーが登録されていません
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={roleConfig[user.role].label}
                          color={roleConfig[user.role].color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusConfig[user.status].label}
                          color={statusConfig[user.status].color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString('ja-JP') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => openRoleChangeDialog(user)}
                          title="権限変更"
                        >
                          <AdminIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(user)}
                          title="削除"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={handleInvite}>
          <DialogTitle>ユーザーを招待</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="メールアドレス"
              type="email"
              placeholder="例: user@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              sx={{ mt: 1, mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>ロール</InputLabel>
              <Select
                value={inviteRole}
                label="ロール"
                onChange={(e) => setInviteRole(e.target.value as UserRole)}
              >
                <MenuItem value="user">一般ユーザー</MenuItem>
                <MenuItem value="reviewer">レビュアー</MenuItem>
                <MenuItem value="admin">管理者</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteOpen(false)}>キャンセル</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting || !inviteEmail}>
              {isSubmitting ? <CircularProgress size={24} /> : '招待を送信'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleChangeOpen} onClose={() => setRoleChangeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>権限変更</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedUser?.name} の権限を変更します
          </Typography>
          <FormControl fullWidth>
            <InputLabel>新しいロール</InputLabel>
            <Select
              value={newRole}
              label="新しいロール"
              onChange={(e) => setNewRole(e.target.value as UserRole)}
            >
              <MenuItem value="user">一般ユーザー</MenuItem>
              <MenuItem value="reviewer">レビュアー</MenuItem>
              <MenuItem value="admin">管理者</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleChangeOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleRoleChange} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : '変更'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>ユーザー削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedUser?.name}（{selectedUser?.email}）を削除してもよろしいですか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
